/**
 * Undo/Redo System
 * 
 * Manages undo/redo history for the city planner.
 * Ported from undo/redo functionality in main.mxml
 */

import { FARM, FARM_OLD } from './Constants.js';

export class UndoRedo {
    constructor(gameEngine, maxHistory = 50) {
        this.gameEngine = gameEngine;
        this.maxHistory = maxHistory;
        this.history = [];
        this.currentIndex = -1;
    }
    
    /**
     * Save current state
     */
    saveState() {
        // Clone current map state
        const state = this.cloneMap(this.gameEngine.map);
        
        // Remove any future states if we're not at the end
        if (this.currentIndex < this.history.length - 1) {
            this.history = this.history.slice(0, this.currentIndex + 1);
        }
        
        // Only save if state is different from current
        if (this.currentIndex >= 0 && this.history.length > 0) {
            const lastState = this.history[this.currentIndex];
            if (this.mapsEqual(state, lastState)) {
                return; // Don't save duplicate states
            }
        }
        
        // Add new state
        this.history.push(state);
        
        // Limit history size
        if (this.history.length > this.maxHistory) {
            this.history.shift();
        } else {
            this.currentIndex++;
        }
    }
    
    /**
     * Check if two maps are equal
     */
    mapsEqual(map1, map2) {
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                if (map1[i][j] !== map2[i][j]) {
                    return false;
                }
            }
        }
        return true;
    }
    
    /**
     * Undo last action
     */
    undo() {
        if (this.canUndo()) {
            this.currentIndex--;
            this.restoreState(this.history[this.currentIndex]);
            return true;
        }
        return false;
    }
    
    /**
     * Redo last undone action
     */
    redo() {
        if (this.canRedo()) {
            this.currentIndex++;
            this.restoreState(this.history[this.currentIndex]);
            return true;
        }
        return false;
    }
    
    /**
     * Check if undo is possible
     */
    canUndo() {
        return this.currentIndex > 0;
    }
    
    /**
     * Check if redo is possible
     */
    canRedo() {
        return this.currentIndex < this.history.length - 1;
    }
    
    /**
     * Clone map array
     */
    cloneMap(map) {
        const clone = [];
        for (let i = 0; i < 21; i++) {
            clone[i] = [];
            for (let j = 0; j < 21; j++) {
                clone[i][j] = map[i][j];
            }
        }
        return clone;
    }
    
    /**
     * Restore map state
     */
    restoreState(state) {
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                this.gameEngine.map[i][j] = state[i][j];
            }
        }
        
        // Recalculate wheat fields based on farms in the restored state
        // First clear all wheat fields
        this.gameEngine.wheatFields = this.gameEngine.createEmptyMap();
        
        // Then recreate wheat fields for all farms in the restored map
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                const tile = this.gameEngine.map[i][j];
                if (tile === FARM || tile === FARM_OLD) {
                    this.gameEngine.createWheatFieldsAroundFarm(i, j);
                }
            }
        }
        
        this.gameEngine.updateTotals();
    }
    
    /**
     * Clear history
     */
    clear() {
        this.history = [];
        this.currentIndex = -1;
    }
}

